import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import {
    Add,
    DescriptionOutlined,
    EditOutlined,
    GetAppOutlined,
    ListAltOutlined,
    MoreVert,
    PictureAsPdfOutlined,
    SwapHorizOutlined,
    SyncOutlined
} from '@material-ui/icons';
import PageWrapper from '../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../shared/pagetitle/PageTitle.js';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import BlockUI from '../../../shared/blockui/BlockUI.tsx';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import SimpleMenu from '../../../shared/menu/SimpleMenu.js';
import PageNaoEncontrada from '../../../PageNaoEncontrada.jsx';
import StatusSituacaoInscricao from '../../components/inscricao/StatusSituacaoInscricao.jsx';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import {
    igualFiltro,
    mesmaOrdenacao,
    montarParametrosPaginado,
    opcoesFiltro,
    ORDENACAO_PADRAO
} from '../../components/inscricao/situacaoInscricao.js';
import DialogSituacaoInscricao from '../../dialogs/inscricao/DialogSituacaoInscricao.jsx';
import DialogFichaInscricao from '../../dialogs/inscricao/DialogFichaInscricao.jsx';
import DialogPlanilhaInscritos from '../../dialogs/inscricao/DialogPlanilhaInscritos.jsx';
import DialogFichasProcesso from '../../dialogs/inscricao/DialogFichasProcesso.jsx';
import DialogAtualizarPsInscricoes from '../../dialogs/inscricao/DialogAtualizarPsInscricoes.jsx';
import useDialogState from '../../../../hooks/useDialogState.js';
import useFetch from '../../../../hooks/useFetch.js';
import useCheckModule from '../../../../hooks/useCheckModule.js';
import {
    URL_INSCRICOES_FILTROS,
    URL_INSCRICOES_PAGINADO,
    URL_INSCRICOES_SITUACOES
} from '../../../../constants/index.js';
import {buttonTableCell, containerPaper} from '../../../../utils/styles.js';
import {textoSemRegistros} from '../../components/paginacao.js';

const PARAMS_VAZIOS = {};
const TAMANHO_PAGINA_INICIAL = 5;
const FILTER_OPTIONS = {filterByColumn: true};
const TABLE_CONTAINER_PROPS = {component: 'div'};
const PAGINATE_OPTIONS = {
    resetPageOnRowsChange: true,
    startPage: 0,
    rowsPerPage: TAMANHO_PAGINA_INICIAL,
    onChangePage: () => {},
    onChangeRowsPerPage: () => {}
};

const useStyles = makeStyles(theme => ({
    ...buttonTableCell(theme, 180),
    ...containerPaper(theme),
    // Ações da linha sempre lado a lado, sem quebrar para a linha de baixo.
    acoesLinha: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        whiteSpace: 'nowrap'
    },
    colunaProtocolo: {
        width: theme.spacing(14)
    }
}));

/*
 * Listagem de inscrições do administrativo: paginação, filtros por coluna e ordenação no
 * servidor, exportações (planilha, fichas, atualização de PS) e ações por linha.
 */
export default function PageInscricoes() {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const checkModule = useCheckModule();
    const [fetch, fetchLoading] = useFetch({returnInnerLoadingState: true});
    const [fetchApoio, apoioLoading] = useFetch({returnInnerLoadingState: true});

    const [itens, setItens] = useState([]);
    const [total, setTotal] = useState(0);
    // Até a primeira resposta da lista a tabela mostra "Carregando...", não "Nenhuma inscrição".
    const [listaCarregada, setListaCarregada] = useState(false);
    const [opcoesProcessos, setOpcoesProcessos] = useState(null);
    const [opcoesProgramas, setOpcoesProgramas] = useState(null);
    const [situacoes, setSituacoes] = useState(null);
    const [inscricaoSelecionada, setInscricaoSelecionada] = useState(null);
    const {dialogState, openDialog, closeDialog} = useDialogState({
        situacao: false,
        ficha: false,
        planilha: false,
        fichas: false,
        atualizarPs: false
    });

    const podeConsultar = checkModule('INSCRICAO');
    const podeEditar = checkModule('EDITINSCRI');
    const podeGerarPlanilha = checkModule('CFGERPLINS');
    const podeGerarFichas = checkModule('CFGERFIINS');
    const podeAtualizarPs = checkModule('CFATUPSINS');
    const podeAlterarSituacao = checkModule('EDITSTATUS');

    /*
     * O DataGrid lazy NÃO chama onFetch ao montar (só em filtro, paginação e troca de
     * tamanho) e não repassa a ordenação nem o sentido: a consulta atual fica nestes refs.
     * `fetch` e `buscar` mudam a cada render (a credencial vem do Redux), então os
     * callbacks estáveis das colunas chamam sempre a versão mais recente pelos refs.
     */
    const consultaRef = useRef({filtros: '{}', pagina: 0, tamanho: TAMANHO_PAGINA_INICIAL});
    const ordenacaoRef = useRef(ORDENACAO_PADRAO);
    const requisicaoRef = useRef(0);
    const fetchRef = useRef(fetch);
    const buscarRef = useRef(null);
    const colunasRef = useRef([]);
    fetchRef.current = fetch;

    const buscar = (alteracoes = {}) => {
        consultaRef.current = {...consultaRef.current, ...alteracoes};

        const {filtros, pagina, tamanho} = consultaRef.current;
        const requisicao = ++requisicaoRef.current;

        fetchRef.current(
            URL_INSCRICOES_PAGINADO,
            montarParametrosPaginado(filtros, ordenacaoRef.current, pagina, tamanho),
            resultado => {
                // Resposta de uma consulta já substituída por outra mais nova: descarta.
                if (requisicao === requisicaoRef.current) {
                    setItens(resultado.resultado);
                    setTotal(resultado.total);
                }
            })
            .then(() => setListaCarregada(true));
    };
    buscarRef.current = buscar;

    // Lê a ordenação que o DataGrid mantém nas próprias colunas (campo e sentido já alternado).
    const sincronizarOrdenacao = useCallback(coluna => {
        const nova = {ordem: coluna.field, ascendente: !!coluna.sortAscending};

        if (mesmaOrdenacao(ordenacaoRef.current, nova)) {
            return false;
        }

        ordenacaoRef.current = nova;
        return true;
    }, []);

    /*
     * O clique no cabeçalho só ordena as linhas da página já carregada e não avisa o
     * onFetch. O DataGrid chama customSort a cada comparação; aqui ele serve de gatilho:
     * registra a ordem nova, não altera a ordem local (retorna 0) e pede a página ao servidor.
     */
    const ordenarNoServidor = useCallback(coluna => () => {
        if (sincronizarOrdenacao(coluna)) {
            window.setTimeout(() => buscarRef.current(), 0);
        }
        return 0;
    }, [sincronizarOrdenacao]);

    const onFetch = (filtros, ordemDoGrid, ascendenteDoGrid, pagina, tamanho) => {
        // Se o clique no cabeçalho não gerou comparações (página com uma linha só), alcança aqui.
        const colunaOrdenada = colunasRef.current.find(coluna => coluna.field === ordemDoGrid && coluna.ordered);
        colunaOrdenada && sincronizarOrdenacao(colunaOrdenada);

        buscar({filtros, pagina, tamanho});
    };

    useEffect(() => {
        if (!podeConsultar) {
            return;
        }

        fetchApoio(URL_INSCRICOES_FILTROS, PARAMS_VAZIOS, filtros => {
            setOpcoesProcessos(opcoesFiltro(filtros.processosSeletivos, 'id', 'descricao'));
            setOpcoesProgramas(opcoesFiltro(filtros.programas, 'id', 'descricao'));
        });
        fetchApoio(URL_INSCRICOES_SITUACOES, PARAMS_VAZIOS, setSituacoes);
        buscar();
    }, [podeConsultar]);

    // openDialog muda a cada render; as colunas (memorizadas) chamam sempre a versão atual pelo ref.
    const openDialogRef = useRef(openDialog);
    openDialogRef.current = openDialog;

    // Diálogos das ações da linha: guardam a inscrição escolhida e abrem o diálogo.
    const abrirDialogoInscricao = useCallback((dialogo, inscricao) => () => {
        setInscricaoSelecionada(inscricao);
        openDialogRef.current(dialogo)();
    }, []);

    const colunas = useMemo(() => {
        const ordenacao = ordenacaoRef.current;

        const colunaOrdenavel = (label, field, extras = {}) => {
            const coluna = {
                label,
                field,
                filterable: true,
                sortable: true,
                ordered: ordenacao.ordem === field,
                sortAscending: ordenacao.ordem === field && ordenacao.ascendente,
                ...extras
            };
            coluna.customSort = ordenarNoServidor(coluna);

            return coluna;
        };

        const opcoesLinha = row => {
            const opcoes = [];

            if (podeGerarFichas) {
                opcoes.push({
                    label: 'Exportar ficha de inscrição',
                    icon: GetAppOutlined,
                    onClick: abrirDialogoInscricao('ficha', row)
                });
            }
            if (podeAlterarSituacao) {
                opcoes.push({
                    label: 'Alterar situação',
                    icon: SwapHorizOutlined,
                    onClick: abrirDialogoInscricao('situacao', row)
                });
            }

            return opcoes;
        };

        const lista = [
            colunaOrdenavel('Protocolo', 'id', {className: classes.colunaProtocolo}),
            colunaOrdenavel('CPF', 'cpf', {valueGetter: valor => ouTraco(valor)}),
            colunaOrdenavel('Nome', 'nome', {valueGetter: valor => ouTraco(valor)}),
            colunaOrdenavel('Nome Social', 'nomeSocial', {valueGetter: valor => ouTraco(valor)}),
            {
                label: 'Processo de Seleção',
                field: 'idProcessoSeletivo',
                filterable: true,
                filterOptions: opcoesProcessos || [],
                customFilter: (termo, valor, row) => !!row.processoSeletivo && igualFiltro(termo, row.processoSeletivo.id),
                valueGetter: (valor, row) => ouTraco(row.processoSeletivo && row.processoSeletivo.descricao)
            },
            {
                label: 'Programa',
                field: 'idPrograma',
                filterable: true,
                filterOptions: opcoesProgramas || [],
                customFilter: (termo, valor, row) => !!row.programa && igualFiltro(termo, row.programa.id),
                valueGetter: (valor, row) => ouTraco(row.programa && row.programa.descricao)
            },
            {
                label: 'Situação',
                field: 'situacao',
                filterable: true,
                filterOptions: opcoesFiltro(situacoes, 'codigo', 'descricao'),
                customFilter: (termo, valor) => !!valor && igualFiltro(termo, valor.codigo),
                valueGetter: valor => <StatusSituacaoInscricao situacao={valor}/>
            },
            {
                label: 'Opções',
                filterable: false,
                className: classes.buttonTableCell,
                valueGetter: (valor, row) => {
                    const opcoes = opcoesLinha(row);

                    return (
                        <div className={classes.acoesLinha}>
                            {podeEditar && (
                                <IconButton
                                    aria-label='Editar'
                                    title='Editar'
                                    onClick={() => navigate(`/administrativo/inscricoes/${row.id}`)}>
                                    <EditOutlined/>
                                </IconButton>
                            )}
                            {podeConsultar && (
                                <IconButton
                                    aria-label='Avaliar documentos'
                                    title='Avaliar documentos'
                                    onClick={() => navigate(`/administrativo/inscricoes/${row.id}/documentos`)}>
                                    <DescriptionOutlined/>
                                </IconButton>
                            )}
                            {opcoes.length > 0 && (
                                <SimpleMenu
                                    icon={MoreVert}
                                    iconButton
                                    iconButtonColor={theme.palette.secondary.dark}
                                    menuColor={theme.palette.tertiary.main}
                                    fontColor='white'
                                    label={`Mais opções da inscrição ${row.id}`}
                                    options={opcoes}/>
                            )}
                        </div>
                    );
                }
            }
        ];
        colunasRef.current = lista;

        return lista;
    }, [
        opcoesProcessos, opcoesProgramas, situacoes, podeConsultar, podeEditar, podeGerarFichas,
        podeAlterarSituacao, navigate, abrirDialogoInscricao, ordenarNoServidor, classes, theme
    ]);

    if (!podeConsultar) {
        return <PageNaoEncontrada/>;
    }

    const toolbar = (podeEditar || podeGerarPlanilha || podeGerarFichas || podeAtualizarPs) ? (
        <>
            {podeEditar && (
                <ContainedButton startIcon={<Add/>} onClick={() => navigate('/administrativo/inscricoes/novo')}>
                    Adicionar
                </ContainedButton>
            )}
            {podeGerarPlanilha && (
                <ContainedButton startIcon={<ListAltOutlined/>} onClick={openDialog('planilha')}>
                    Planilha de Inscritos
                </ContainedButton>
            )}
            {podeGerarFichas && (
                <ContainedButton startIcon={<PictureAsPdfOutlined/>} onClick={openDialog('fichas')}>
                    Fichas de Inscrição
                </ContainedButton>
            )}
            {podeAtualizarPs && (
                <ContainedButton startIcon={<SyncOutlined/>} onClick={openDialog('atualizarPs')}>
                    Atualizar PS Inscrições
                </ContainedButton>
            )}
        </>
    ) : undefined;

    return (
        <>
            <PageWrapper>
                <PageTitle title='Inscrições'/>
                {/*
                  * Como no Processo de Seleção: a tabela monta já na abertura, dentro do cartão e coberta
                  * pelo carregamento; os filtros de seleção das colunas recebem as opções quando o apoio chega.
                  */}
                <Paper className={classes.containerPaper}>
                    <BlockUI loading={fetchLoading || apoioLoading}>
                        <DataGrid
                            rows={itens}
                            columns={colunas}
                            filterOptions={FILTER_OPTIONS}
                            sort
                            paginate
                            paginateOptions={PAGINATE_OPTIONS}
                            lazyLoad
                            lazyLoadOptions={{
                                fetchId: 'inscricoes',
                                total,
                                onFetch
                            }}
                            tableContainerProps={TABLE_CONTAINER_PROPS}
                            toolbarContent={toolbar}
                            noRecordsText={textoSemRegistros(fetchLoading || !listaCarregada, 'Nenhuma inscrição encontrada.')}/>
                    </BlockUI>
                </Paper>
            </PageWrapper>
            <DialogSituacaoInscricao
                open={dialogState.situacao}
                onClose={closeDialog('situacao')}
                inscricao={inscricaoSelecionada}
                situacoes={situacoes}
                onSalvo={() => buscarRef.current()}/>
            <DialogFichaInscricao
                open={dialogState.ficha}
                onClose={closeDialog('ficha')}
                inscricao={inscricaoSelecionada}/>
            <DialogPlanilhaInscritos open={dialogState.planilha} onClose={closeDialog('planilha')}/>
            <DialogFichasProcesso open={dialogState.fichas} onClose={closeDialog('fichas')}/>
            <DialogAtualizarPsInscricoes open={dialogState.atualizarPs} onClose={closeDialog('atualizarPs')}/>
        </>
    );
}
